import { config } from './config';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

let loader: Promise<void> | null = null;

/**
 * Loads Google reCAPTCHA v3 on demand.
 *
 * v3 is invisible, so it does not change the form layout - which matters because the
 * Figma mock-up has no captcha widget. Verification always happens server-side; the
 * token produced here is worthless on its own.
 */
function load(): Promise<void> {
  if (!config.recaptchaSiteKey) return Promise.reject(new Error('recaptcha-not-configured'));
  if (loader) return loader;

  loader = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha]');
    if (existing) { resolve(); return; }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(config.recaptchaSiteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptcha = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('recaptcha-load-failed'));
    document.head.appendChild(script);
  });

  return loader;
}

export async function getCaptchaToken(action: string): Promise<string> {
  await load();
  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) throw new Error('recaptcha-unavailable');
  await new Promise<void>((resolve) => grecaptcha.ready(resolve));
  return grecaptcha.execute(config.recaptchaSiteKey, { action });
}

export const isCaptchaConfigured = () => Boolean(config.recaptchaSiteKey);
